import { normalizeAustralianPhoneToLocal } from '@/lib/auPhone';

export interface ParsedOrderContact {
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  serviceAddress: string;
}

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const PHONE_RE =
  /(?:\+?\s*61[\s\-()]*)?(?:0[\s\-()]*)?(?:4\d{2}[\s\-()]*\d{3}[\s\-()]*\d{3}|0[2378][\s\-()]*\d{4}[\s\-()]*\d{4})/;

const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const STATE_POSTCODE_RE = new RegExp(`\\b(${AU_STATES.join('|')})\\b\\s*\\d{4}\\b`, 'i');
const POSTCODE_RE = /\b\d{4}\b/;
const STREET_HINT_RE =
  /\b\d+[A-Za-z]?\s+\S+(?:\s+\S+)*\s+(?:street|st|road|rd|avenue|ave|drive|dr|court|ct|parade|pde|lane|ln|way|crescent|cres|place|pl|boulevard|blvd)\b/i;

const NAME_LABELS = ['full name', 'name', 'customer name', 'customer', 'contact name', 'contact'];
const PHONE_LABELS = ['phone', 'mobile', 'tel', 'telephone', 'phone number', 'mobile number'];
const EMAIL_LABELS = ['email', 'e-mail', 'email address'];
const ADDRESS_LABELS = ['service address', 'address', 'location', 'service location', 'property address'];

function normalizeText(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
}

function parseLabeledLine(line: string): { key: string; value: string } | null {
  const match = line.match(/^\s*([^:：\-]+?)\s*[:：\-]\s*(.+)\s*$/);
  if (!match) return null;
  return { key: match[1].trim().toLowerCase(), value: match[2].trim() };
}

function pickLabelValue(key: string, labels: string[]): boolean {
  return labels.some((label) => key === label || key.endsWith(` ${label}`) || key.startsWith(`${label} `));
}

function formatPhone(raw: string): string {
  const local = normalizeAustralianPhoneToLocal(raw);
  if (!local) return raw.trim();
  if (/^04\d{8}$/.test(local)) {
    return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
  }
  return `${local.slice(0, 2)} ${local.slice(2, 6)} ${local.slice(6)}`;
}

function looksLikeName(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length < 2 || trimmed.length > 80) return false;
  if (EMAIL_RE.test(trimmed) || PHONE_RE.test(trimmed)) return false;
  if (STATE_POSTCODE_RE.test(trimmed) || STREET_HINT_RE.test(trimmed)) return false;
  if (/\d{3,}/.test(trimmed)) return false;
  return /^[A-Za-z][A-Za-z\s'.-]+$/.test(trimmed);
}

function looksLikeAddressLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  return STATE_POSTCODE_RE.test(trimmed) || STREET_HINT_RE.test(trimmed) || POSTCODE_RE.test(trimmed);
}

function extractAddressBlock(lines: string[], startIndex: number): string {
  const labeled = parseLabeledLine(lines[startIndex]);
  const parts = [labeled ? labeled.value : lines[startIndex].trim()];
  for (let i = startIndex + 1; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line) break;
    if (parseLabeledLine(line)) break;
    if (EMAIL_RE.test(line) || PHONE_RE.test(line)) break;
    parts.push(line);
  }
  return parts.join(', ');
}

function findAddressHeuristic(lines: string[]): string {
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line || parseLabeledLine(line)) continue;
    if (!looksLikeAddressLine(line)) continue;

    if (i > 0) {
      const prev = lines[i - 1].trim();
      if (prev && !parseLabeledLine(prev) && !EMAIL_RE.test(prev) && !PHONE_RE.test(prev) && !looksLikeName(prev)) {
        return `${prev}, ${line}`;
      }
    }
    return line;
  }
  return '';
}

/** Extract customer contact fields from pasted free-form text. */
export function parseOrderContactText(text: string): ParsedOrderContact {
  const normalized = normalizeText(text);
  const lines = normalized.split('\n').map((line) => line.trim()).filter(Boolean);

  let customerName = '';
  let customerPhone = '';
  let customerEmail = '';
  let serviceAddress = '';

  for (let i = 0; i < lines.length; i += 1) {
    const labeled = parseLabeledLine(lines[i]);
    if (!labeled) continue;

    if (!customerName && pickLabelValue(labeled.key, NAME_LABELS)) {
      customerName = labeled.value;
      continue;
    }
    if (!customerPhone && pickLabelValue(labeled.key, PHONE_LABELS)) {
      customerPhone = formatPhone(labeled.value);
      continue;
    }
    if (!customerEmail && pickLabelValue(labeled.key, EMAIL_LABELS)) {
      customerEmail = labeled.value;
      continue;
    }
    if (!serviceAddress && pickLabelValue(labeled.key, ADDRESS_LABELS)) {
      serviceAddress = extractAddressBlock(lines, i);
    }
  }

  if (!customerEmail) {
    const emailMatch = normalized.match(EMAIL_RE);
    if (emailMatch) customerEmail = emailMatch[0];
  }

  if (!customerPhone) {
    const phoneMatch = normalized.match(PHONE_RE);
    if (phoneMatch) customerPhone = formatPhone(phoneMatch[0]);
  }

  if (!serviceAddress) {
    for (let i = 0; i < lines.length; i += 1) {
      const labeled = parseLabeledLine(lines[i]);
      if (labeled && pickLabelValue(labeled.key, ADDRESS_LABELS)) {
        serviceAddress = extractAddressBlock(lines, i);
        break;
      }
    }
  }

  if (!serviceAddress) {
    serviceAddress = findAddressHeuristic(lines);
  }

  if (!customerName) {
    for (const line of lines) {
      if (parseLabeledLine(line)) continue;
      if (EMAIL_RE.test(line) || PHONE_RE.test(line)) continue;
      if (looksLikeAddressLine(line)) continue;
      if (looksLikeName(line)) {
        customerName = line.trim();
        break;
      }
    }
  }

  return {
    customerName,
    customerPhone,
    customerEmail,
    serviceAddress: serviceAddress.trim(),
  };
}

export function hasRecognizedContact(parsed: ParsedOrderContact): boolean {
  return Boolean(
    parsed.customerName ||
      parsed.customerPhone ||
      parsed.customerEmail ||
      parsed.serviceAddress,
  );
}
